import React from 'react';
import LegalLayout from './LegalLayout';

/**
 * Datenschutzerklärung (DSGVO + LOPDGDD).
 *
 * Adapted from REALTECHSOLUTIONS' existing data-protection text used on
 * other products, retargeted for Real Content's specific processor list.
 * Spanish company → Spanish + EU data-protection law applies; AEPD is
 * the supervisory authority. We disclose the Spanish provenance where
 * the law requires (responsible party, complaint authority) and keep
 * everything else focused on what the user actually needs to know about
 * how their data is handled.
 */
export default function DatenschutzPage() {
  return (
    <LegalLayout title="Datenschutzerklärung">
      <p>
        <strong>Stand:</strong> 29. April 2026
      </p>

      <h2>Einleitung und Überblick</h2>
      <p>
        Diese Datenschutzerklärung erläutert auf Basis der Datenschutz-
        Grundverordnung (EU) 2016/679 (DSGVO) und der geltenden nationalen
        Gesetze, welche personenbezogenen Daten wir und unsere
        Auftragsverarbeiter verarbeiten, zu welchem Zweck und wie lange.
        Sie informiert außerdem über die Rechte, die Ihnen als betroffener
        Person zustehen.
      </p>

      <h2>Anwendungsbereich</h2>
      <p>
        Diese Erklärung gilt für alle personenbezogenen Daten, die wir
        beim Betrieb der Real-Content-Plattform verarbeiten. Also bei
        der Nutzung der Webseite, der App-Funktionen, der Kommunikation
        per E-Mail sowie bei der Anbindung von Drittdiensten (z. B.
        Justimmo, Stripe, soziale Netzwerke).
      </p>

      <h2>Verantwortlicher</h2>
      <p>
        REALTECHSOLUTIONS, S.L.<br />
        Calle Galicia, Num 9, Bloque B, Planta Baja, Puerta 3<br />
        35660 Corralejo, La Oliva, Fuerteventura, Spanien<br />
        E-Mail: <a href="mailto:info@real-content.at">info@real-content.at</a>
      </p>

      <h2>Rechtsgrundlagen</h2>
      <p>
        Wir verarbeiten Ihre Daten nur, wenn mindestens eine der
        folgenden Rechtsgrundlagen zutrifft:
      </p>
      <ul>
        <li><strong>Vertragserfüllung</strong> (Art. 6 Abs. 1 lit. b DSGVO)
         . Z. B. Bereitstellung der SaaS-Funktionen, Abwicklung der
          Bezahlung, Veröffentlichung von Auto-Posts.</li>
        <li><strong>Einwilligung</strong> (Art. 6 Abs. 1 lit. a DSGVO)
         . Z. B. für optionale Cookies, Empfang der Vorschau-E-Mails.</li>
        <li><strong>Rechtliche Verpflichtung</strong> (Art. 6 Abs. 1 lit. c DSGVO)
         . Z. B. steuerliche Aufbewahrungspflichten für Rechnungen.</li>
        <li><strong>Berechtigte Interessen</strong> (Art. 6 Abs. 1 lit. f DSGVO)
         . Z. B. Sicherheit, Missbrauchsschutz, Fehlerdiagnose.</li>
      </ul>
      <p>
        Da das Unternehmen seinen Sitz in Spanien hat, gilt zusätzlich
        die Ley Orgánica 3/2018, de 5 de diciembre, de Protección de
        Datos Personales y garantía de los derechos digitales (LOPDGDD).
      </p>

      <h2>Welche Daten wir verarbeiten</h2>
      <ul>
        <li><strong>Account-Daten:</strong> Name, E-Mail-Adresse,
          Passwort-Hash (bcrypt), Profilbild, Markenname, Telefonnummer.</li>
        <li><strong>Immobiliendaten:</strong> Adressen, Beschreibungen,
          Bilder, Preise. Importiert aus Ihrer Justimmo-API-Anbindung.</li>
        <li><strong>Erstellte Videos</strong> sowie deren
          Veröffentlichungsstatus pro sozialer Plattform.</li>
        <li><strong>Zahlungsdaten</strong> über Stripe (Karten-Daten
          werden ausschließlich bei Stripe gespeichert; wir erhalten
          nur eine Token-ID und Rechnungs-Metadaten).</li>
        <li><strong>Technische Logs:</strong> IP-Adresse, User-Agent,
          Zeitstempel. Zur Sicherheits- und Fehlerdiagnose.</li>
        <li><strong>Nutzungsmetriken</strong> (cookielos via Plausible
          Analytics). Seitenaufrufe und Conversion-Schritte ohne
          personenbezogene Identifikation.</li>
      </ul>

      <h2>Auftragsverarbeiter</h2>
      <p>
        Wir setzen folgende Dienstleister ein, mit denen jeweils ein
        Auftragsverarbeitungsvertrag (AVV) gemäß Art. 28 DSGVO besteht.
        Datenübermittlungen in Drittländer (insbesondere USA) erfolgen
        nur auf Basis der EU-Standardvertragsklauseln (SCCs) und –
        soweit verfügbar. Der Teilnahme am EU-US Data Privacy Framework.
      </p>
      <ul>
        <li><strong>Stripe Payments Europe Ltd.</strong> (Irland / USA). Zahlungsabwicklung</li>
        <li><strong>Creatomate B.V.</strong> (Niederlande). Video-Rendering</li>
        <li><strong>ElevenLabs Inc.</strong> (USA). KI-Sprachsynthese (Voiceover), SCCs</li>
        <li><strong>OpenAI Ireland Ltd.</strong> (Irland). Generierung von Captions und Voiceover-Texten, SCCs</li>
        <li><strong>Justimmo Software Solutions GmbH</strong> (Österreich). Import Ihrer Immobilieninserate</li>
        <li><strong>Upload-Post Inc.</strong> (USA). Veröffentlichung auf Social-Media-Plattformen, SCCs</li>
        <li><strong>Cloudflare, Inc.</strong> (USA). Content Delivery Network und DDoS-Schutz, SCCs</li>
        <li><strong>MongoDB Inc.</strong> (Atlas, EU-Region). Datenbank-Hosting</li>
        <li><strong>Hetzner Online GmbH</strong> (Deutschland). Server-Hosting</li>
        <li><strong>World4You Internet Services GmbH</strong> (Österreich). E-Mail-Hosting</li>
        <li><strong>Plausible Insights OÜ</strong> (Estland). Selbst-gehostete Reichweitenmessung (cookielos)</li>
      </ul>

      <h2>Speicherdauer</h2>
      <p>
        Wir speichern Ihre personenbezogenen Daten nur so lange, wie es
        für den jeweiligen Zweck erforderlich ist:
      </p>
      <ul>
        <li>Account-Daten: für die Dauer Ihres aktiven Abonnements
          zuzüglich 30 Tagen.</li>
        <li>Immobiliendaten: ebenso, plus automatische Löschung von
          Datensätzen, die in den letzten 6 Monaten weder synchronisiert
          noch beworben wurden.</li>
        <li>Generierte Videos: 30 Tage als URL-Cache; danach Löschung
          des Datensatzes.</li>
        <li>Rechnungsbelege: 10 Jahre gemäß steuerrechtlicher
          Aufbewahrungspflichten.</li>
        <li>Bei Kontolöschung (DSGVO Art. 17): vollständige Löschung
          nach einer 7-tägigen Karenzzeit, in der Sie die Löschung per
          E-Mail rückgängig machen können.</li>
      </ul>

      <h2>Ihre Rechte gemäß DSGVO</h2>
      <ul>
        <li><strong>Auskunftsrecht</strong> (Art. 15). Sie können eine
          Kopie aller über Sie gespeicherten Daten anfordern. Im Profil-
          Bereich finden Sie dazu den Button „Meine Daten herunterladen"
          (ZIP-Export).</li>
        <li><strong>Recht auf Berichtigung</strong> (Art. 16). Direkt
          im Profil änderbar.</li>
        <li><strong>Recht auf Löschung</strong> (Art. 17). Über
          „Konto löschen" im Profil.</li>
        <li><strong>Recht auf Einschränkung</strong> (Art. 18).</li>
        <li><strong>Recht auf Datenübertragbarkeit</strong> (Art. 20) –
          siehe ZIP-Export oben.</li>
        <li><strong>Widerspruchsrecht</strong> (Art. 21). Per E-Mail
          an <a href="mailto:info@real-content.at">info@real-content.at</a>.</li>
      </ul>
      <p>
        Sie können sich außerdem bei einer Datenschutzaufsichtsbehörde
        beschweren. Entweder bei der spanischen{' '}
        <a href="https://www.aepd.es" target="_blank" rel="noreferrer noopener">
          Agencia Española de Protección de Datos (AEPD)
        </a>
        {' '}oder bei der österreichischen{' '}
        <a href="https://www.dsb.gv.at" target="_blank" rel="noreferrer noopener">
          Datenschutzbehörde
        </a>.
      </p>

      <h2>Sicherheit der Datenverarbeitung</h2>
      <p>
        Wir setzen technische und organisatorische Maßnahmen ein, um
        Ihre Daten zu schützen. TLS-verschlüsselte Übertragung,
        Passwort-Hashing mit bcrypt, Zugangskontrollen für Mitarbeitende,
        regelmäßige Sicherheitsüberprüfungen sowie Server-Hosting in
        Rechenzentren innerhalb der EU.
      </p>

      <h2>Cookies</h2>
      <p>
        Wir verwenden technisch notwendige Cookies für Login und
        Sitzungsverwaltung (Rechtsgrundlage: berechtigtes Interesse,
        Art. 6 Abs. 1 lit. f DSGVO). Optionale Cookies. Etwa für
        Sentry-Fehlerdiagnose. Setzen wir erst nach Ihrer Einwilligung
        über das Cookie-Banner. Plausible Analytics arbeitet vollständig
        ohne Cookies. Sie können Ihre Cookie-Auswahl jederzeit über das
        Banner-Icon im Footer anpassen.
      </p>

      <h2>Datenübertragung in Drittländer</h2>
      <p>
        Einige unserer Dienstleister haben ihren Sitz außerhalb der EU,
        insbesondere in den USA (Stripe, ElevenLabs, OpenAI, Upload-Post,
        Cloudflare). Diese Übermittlungen erfolgen ausschließlich auf
        Basis der EU-Standardvertragsklauseln (SCCs) gemäß Art. 46 DSGVO
        sowie. Wo verfügbar. Der Teilnahme am EU-US Data Privacy
        Framework. Trotz dieser Schutzmaßnahmen können wir nicht
        ausschließen, dass US-Behörden im Rahmen ihrer Gesetze auf
        Daten zugreifen. Mit der Nutzung unserer Plattform akzeptieren
        Sie diese Verarbeitung.
      </p>

      <h2>Beschwerden bei der AEPD</h2>
      <p>
        Wenn Sie der Meinung sind, dass Ihre Datenschutzrechte verletzt
        wurden, können Sie eine Beschwerde bei der spanischen Agencia
        Española de Protección de Datos einreichen:{' '}
        <a href="https://www.aepd.es" target="_blank" rel="noreferrer noopener">
          www.aepd.es
        </a>.
      </p>

      <h2>Aktualisierung dieser Erklärung</h2>
      <p>
        Wir aktualisieren diese Datenschutzerklärung, wenn sich
        Verarbeitungsprozesse oder rechtliche Rahmenbedingungen ändern.
        Bei wesentlichen Änderungen informieren wir Sie per E-Mail.
        Stand dieser Erklärung: <strong>29. April 2026</strong>.
      </p>
    </LegalLayout>
  );
}
